/**
 * PLAT-11445 — pure helpers for the media a clip composition carries while
 * its story's video renditions are still producing, and for re-pointing it:
 *
 *   editor audio (mp3, black canvas)
 *     → the session's local original (renderer CacheStorage, LocalCanvasPreview)
 *     → the preview rendition once it lands.
 *
 * Creator Studio does the same swaps on VideoCreatorStore; the Clip Editor
 * drives them through ClipVideoCreatorAdapter. Everything here is
 * side-effect free apart from mutating the element records handed in.
 */
import { isLocalCanvasSource } from '@src/services/LocalCanvasPreview';
import { isSameMediaFile } from '@src/utility/mediaUrl';
import { pickUnderlyingVideoElements } from '@src/utility/stackedLayout';
import { v4 as uuid } from 'uuid';

// Composition element records are untyped JSON throughout the editor.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SourceElement = Record<string, any>;

export type CompositionMediaKind =
  | 'audio-first'
  | 'local-canvas'
  | 'video'
  | 'none';

/** Whether `source` is the story's editor audio (path identity). */
export function isAudioFirstSource(
  source: unknown,
  audioUrl: string | null | undefined,
): boolean {
  return !!audioUrl && isSameMediaFile(source, audioUrl);
}

/**
 * Every video element in the composition, looking through the composition
 * wrappers splits/scenes introduce. Tolerates malformed records.
 */
export function underlyingVideoElements(elements: unknown): SourceElement[] {
  if (!Array.isArray(elements)) return [];
  const out: SourceElement[] = [];
  for (const element of elements) {
    if (!element || typeof element !== 'object') continue;
    try {
      for (const inner of pickUnderlyingVideoElements(element)) {
        if (inner && typeof inner === 'object') out.push(inner);
      }
    } catch {
      /* malformed wrapper — skip */
    }
  }
  return out;
}

/**
 * Replace original-video sources with the editor audio while renditions are
 * pending. Generated clips commonly wrap their video in composition elements,
 * so this must inspect the same underlying elements as the later local/rendition
 * swaps. Element ids stay stable because this is the initial playable fallback,
 * not a media-kind swap in an already-painted renderer.
 */
export function bootstrapAudioFirstMedia(
  elements: unknown,
  isOriginal: (element: SourceElement) => boolean,
  audioUrl: string,
): boolean {
  let changed = false;
  for (const inner of underlyingVideoElements(elements)) {
    if (
      inner.type !== 'video' ||
      !isOriginal(inner) ||
      isAudioFirstSource(inner.source, audioUrl) ||
      isLocalCanvasSource(inner.source)
    ) {
      continue;
    }
    inner.source = audioUrl;
    changed = true;
  }
  return changed;
}

/**
 * What the composition's media elements currently point at. 'video' means
 * at least one element sources something other than the editor audio or the
 * local canvas (a rendition or the original); audio/local win when every
 * media element is in that state.
 */
export function classifyCompositionMedia(
  elements: unknown,
  audioUrl: string | null | undefined,
): CompositionMediaKind {
  const inner = underlyingVideoElements(elements).filter(
    el => typeof el.source === 'string' && el.source.length > 0,
  );
  if (inner.length === 0) return 'none';
  if (
    inner.some(
      el =>
        !isAudioFirstSource(el.source, audioUrl) &&
        !isLocalCanvasSource(el.source),
    )
  ) {
    return 'video';
  }
  if (inner.some(el => isLocalCanvasSource(el.source))) return 'local-canvas';
  return 'audio-first';
}

/**
 * Point every matching media element at `toUrl`. A fresh id forces the
 * renderer to recreate the element's media — it caches per-element media
 * state, and a source-only change from the mp3 keeps its zero video
 * dimensions (black canvas). Returns old id → new id for every element
 * changed (empty when nothing changed).
 */
export function repointMediaElements(
  elements: unknown,
  matches: (element: SourceElement) => boolean,
  toUrl: string,
): Map<string, string> {
  const idMap = new Map<string, string>();
  for (const inner of underlyingVideoElements(elements)) {
    if (!matches(inner)) continue;
    if (inner.source === toUrl) continue;
    const oldId = inner.id != null ? String(inner.id) : uuid();
    const newId = uuid();
    inner.type = 'video';
    inner.source = toUrl;
    inner.id = newId;
    idMap.set(oldId, newId);
  }
  return idMap;
}

/**
 * Per-element metadata is keyed by element id (and `logo_el_<id>`); after a
 * repoint the keys follow the ids. Returns a new record.
 */
export function remapElementMetaIds<T>(
  meta: Record<string, T> | null | undefined,
  idMap: Map<string, string>,
): Record<string, T> {
  const out: Record<string, T> = {};
  if (!meta) return out;
  for (const [key, value] of Object.entries(meta)) {
    const logo = key.startsWith('logo_el_')
      ? key.slice('logo_el_'.length)
      : null;
    if (logo != null && idMap.has(logo)) {
      out[`logo_el_${idMap.get(logo)!}`] = value;
    } else if (idMap.has(key)) {
      out[idMap.get(key)!] = value;
    } else {
      out[key] = value;
    }
  }
  return out;
}

/** Matcher for elements still on the editor audio or the local canvas. */
export function isPendingEraElement(
  audioUrl: string | null | undefined,
): (element: SourceElement) => boolean {
  return element =>
    isAudioFirstSource(element.source, audioUrl) ||
    isLocalCanvasSource(element.source);
}
